import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Download, Plus, Trash2, ArrowLeft, Sparkles, RotateCcw, Building2 , Save, Clock , FileText, FileUp, FileDown} from "lucide-react";
import { useAuth, useSettings } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LessonsLearnedForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: "",
    date: new Date().toISOString().split("T")[0],
    facilitator: "",
    attendees: [""],
    what_went_well: [{ category: "", description: "", impact: "" }],
    what_could_improve: [{ category: "", description: "", root_cause: "" }],
    recommendations: [""],
    action_items: [{ action: "", owner: "", due_date: "" }]
  });

  const getSampleData = () => ({
    project_name: "Mobile App Launch Project",
    date: new Date().toISOString().split("T")[0],
    facilitator: "Sarah Johnson",
    attendees: ["John Smith", "Mike Chen", "Lisa Wang", "Tom Brown", "Amy Davis"],
    what_went_well: [
      { category: "Planning", description: "Detailed sprint planning sessions improved predictability", impact: "Delivered 95% of planned features" },
      { category: "Communication", description: "Daily standups kept everyone aligned", impact: "Issues identified and resolved quickly" },
      { category: "Testing", description: "Automated testing reduced bug escape rate", impact: "90% fewer production issues vs previous projects" },
      { category: "Collaboration", description: "Cross-functional team worked seamlessly", impact: "Faster decision making and fewer handoff delays" }
    ],
    what_could_improve: [
      { category: "Requirements", description: "Some requirements were vague initially", root_cause: "Stakeholder availability was limited during discovery" },
      { category: "Estimation", description: "Integration tasks took longer than estimated", root_cause: "Legacy API documentation was outdated" },
      { category: "Resources", description: "Team was stretched thin during peak periods", root_cause: "Parallel projects competed for same resources" }
    ],
    recommendations: [
      "Implement mandatory requirements workshops with all stakeholders before project kickoff",
      "Add buffer time for integration work when dealing with legacy systems",
      "Establish resource allocation agreements at portfolio level before project start",
      "Create a knowledge base for lessons learned accessible to all project teams",
      "Schedule mid-project retrospectives for longer projects"
    ],
    action_items: [
      { action: "Create requirements workshop template", owner: "BA Team", due_date: "2025-02-15" },
      { action: "Update estimation guidelines for integration work", owner: "Tech Lead", due_date: "2025-02-01" },
      { action: "Propose resource management improvements to PMO", owner: "Project Manager", due_date: "2025-02-10" },
      { action: "Set up lessons learned knowledge base", owner: "PMO", due_date: "2025-03-01" }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Lessons Learned Draft";
      const payload = {
        name: draftName,
        template_type: "Lessons Learned",
        template_id: "lessons-learned",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "lessons-learned", "Lessons Learned", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("lessons-learned", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/lessons-learned`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const response = await axios.post(url, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Lessons_Learned_${formData.project_name || "Document"}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Lessons Learned",
        formData,
        settings: activeSettings || {},
        templateType: "Lessons Learned",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateWentWell = (index, field, value) => {
    const updated = [...formData.what_went_well];
    updated[index][field] = value;
    setFormData({ ...formData, what_went_well: updated });
  };

  const addWentWell = () => {
    setFormData({
      ...formData,
      what_went_well: [...formData.what_went_well, { category: "", description: "", impact: "" }]
    });
  };

  const removeWentWell = (index) => {
    setFormData({ ...formData, what_went_well: formData.what_went_well.filter((_, i) => i !== index) });
  };

  const updateCouldImprove = (index, field, value) => {
    const updated = [...formData.what_could_improve];
    updated[index][field] = value;
    setFormData({ ...formData, what_could_improve: updated });
  };

  const addCouldImprove = () => {
    setFormData({
      ...formData,
      what_could_improve: [...formData.what_could_improve, { category: "", description: "", root_cause: "" }]
    });
  };

  const removeCouldImprove = (index) => {
    setFormData({ ...formData, what_could_improve: formData.what_could_improve.filter((_, i) => i !== index) });
  };

  const updateListItem = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addListItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const removeListItem = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const updateActionItem = (index, field, value) => {
    const updated = [...formData.action_items];
    updated[index][field] = value;
    setFormData({ ...formData, action_items: updated });
  };

  const addActionItem = () => {
    setFormData({
      ...formData,
      action_items: [...formData.action_items, { action: "", owner: "", due_date: "" }]
    });
  };

  const removeActionItem = (index) => {
    setFormData({ ...formData, action_items: formData.action_items.filter((_, i) => i !== index) });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Lessons Learned</h1>
                <p className="text-sm text-gray-500">Capture insights for future project improvement</p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Header/Footer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Session Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} placeholder="Project name" data-testid="project-name-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div>
                <Label>Facilitator</Label>
                <Input value={formData.facilitator} onChange={(e) => setFormData({ ...formData, facilitator: e.target.value })} placeholder="Session facilitator" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Attendees</Label>
              {formData.attendees.map((attendee, index) => (
                <div key={index} className="flex gap-2">
                  <Input value={attendee} onChange={(e) => updateListItem("attendees", index, e.target.value)} placeholder="Attendee name" />
                  <Button variant="ghost" size="icon" onClick={() => removeListItem("attendees", index)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addListItem("attendees")}>
                <Plus className="h-4 w-4 mr-2" /> Add Attendee
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold text-green-700">What Went Well</Label>
              {formData.what_went_well.map((item, index) => (
                <Card key={index} className="p-4 bg-green-50 border-green-200">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={item.category} onValueChange={(v) => updateWentWell(index, "category", v)}>
                        <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Planning">Planning</SelectItem>
                          <SelectItem value="Communication">Communication</SelectItem>
                          <SelectItem value="Testing">Testing</SelectItem>
                          <SelectItem value="Collaboration">Collaboration</SelectItem>
                          <SelectItem value="Technical">Technical</SelectItem>
                          <SelectItem value="Process">Process</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input value={item.description} onChange={(e) => updateWentWell(index, "description", e.target.value)} placeholder="What happened" />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label>Impact</Label>
                        <Input value={item.impact} onChange={(e) => updateWentWell(index, "impact", e.target.value)} placeholder="Positive outcome" />
                      </div>
                      <div className="flex items-end">
                        <Button variant="ghost" size="icon" onClick={() => removeWentWell(index)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addWentWell}>
                <Plus className="h-4 w-4 mr-2" /> Add Item
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold text-orange-700">What Could Improve</Label>
              {formData.what_could_improve.map((item, index) => (
                <Card key={index} className="p-4 bg-orange-50 border-orange-200">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={item.category} onValueChange={(v) => updateCouldImprove(index, "category", v)}>
                        <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Requirements">Requirements</SelectItem>
                          <SelectItem value="Estimation">Estimation</SelectItem>
                          <SelectItem value="Resources">Resources</SelectItem>
                          <SelectItem value="Communication">Communication</SelectItem>
                          <SelectItem value="Technical">Technical</SelectItem>
                          <SelectItem value="Process">Process</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input value={item.description} onChange={(e) => updateCouldImprove(index, "description", e.target.value)} placeholder="What happened" />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label>Root Cause</Label>
                        <Input value={item.root_cause} onChange={(e) => updateCouldImprove(index, "root_cause", e.target.value)} placeholder="Why it happened" />
                      </div>
                      <div className="flex items-end">
                        <Button variant="ghost" size="icon" onClick={() => removeCouldImprove(index)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addCouldImprove}>
                <Plus className="h-4 w-4 mr-2" /> Add Item
              </Button>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="font-semibold">Recommendations</Label>
              {formData.recommendations.map((rec, index) => (
                <div key={index} className="flex gap-2">
                  <Input value={rec} onChange={(e) => updateListItem("recommendations", index, e.target.value)} placeholder="Recommendation" />
                  <Button variant="ghost" size="icon" onClick={() => removeListItem("recommendations", index)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addListItem("recommendations")}>
                <Plus className="h-4 w-4 mr-2" /> Add Recommendation
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Action Items</Label>
              {formData.action_items.map((item, index) => (
                <div key={index} className="grid grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label>Action</Label>
                    <Input value={item.action} onChange={(e) => updateActionItem(index, "action", e.target.value)} placeholder="Action to take" />
                  </div>
                  <div>
                    <Label>Owner</Label>
                    <Input value={item.owner} onChange={(e) => updateActionItem(index, "owner", e.target.value)} placeholder="Responsible person" />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label>Due Date</Label>
                      <Input type="date" value={item.due_date} onChange={(e) => updateActionItem(index, "due_date", e.target.value)} />
                    </div>
                    <div className="flex items-end">
                      <Button variant="ghost" size="icon" onClick={() => removeActionItem(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={addActionItem}>
                <Plus className="h-4 w-4 mr-2" /> Add Action Item
              </Button>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LessonsLearnedForm;
